
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdmissionForm from './AdmissionForm';
import StudentAvatar from '../components/StudentAvatar';
import { 
  Users, 
  Search, 
  Plus, 
  Mail, 
  Phone,
  FileText,
  Edit3,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2
} from 'lucide-react';

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Pagination State
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form State
  const [showForm, setShowForm] = useState(false);

  const fetchStudents = async () => {
    try {
      const res = await api.get('/students');
      setStudents(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Permanently delete ${student.full_name}? This cannot be undone.`)) return;
    try {
        await api.delete(`/students/${student.id}`);
        alert('Student deleted successfully');
        fetchStudents();
    } catch (err) {
        alert('Error deleting student. They might have active applications or payments.');
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Extract Search & Filter Logic
  const filteredStudents = students.filter(s => 
    (s.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (s.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
    (s.passport_no?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const paginatedStudents = filteredStudents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading Students...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Student List</h1>
          <p className="text-sm text-gray-500 font-semibold">Manage and view all registered students and applicants.</p>
        </div>
        
        <div className="flex gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
               <Search className="absolute left-4 top-2.5 text-gray-400" size={18} />
               <input 
                  type="text" 
                  placeholder="Search students..." 
                  className="input-field pl-12"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
               />
            </div>
            <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
                {showForm ? <X size={18} /> : <Plus size={18} />}
                {showForm ? 'Cancel' : 'Add Student'}
            </button>
        </div>
      </div>

      {showForm && (
        <div className="animate-in slide-in-from-top-4 duration-300">
           <AdmissionForm 
              onSuccess={() => {
                  fetchStudents();
                  setShowForm(false);
              }} 
              onCancel={() => setShowForm(false)} 
           />
        </div>
      )}

      {!showForm && (
        <div className="card !p-0 overflow-hidden border-b-4 border-b-blue-500 shadow-sm">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[22%] min-w-[180px]">Student Name</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[22%] min-w-[180px]">Contact Info</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[13%] min-w-[110px]">Visa Type</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[13%] min-w-[110px]">Country</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[12%] min-w-[110px]">Created By</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedStudents.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-20 text-center">
                       <Users className="mx-auto text-gray-100 mb-4" size={48} />
                       <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No matching student records found</p>
                    </td>
                  </tr>
                )}
                {paginatedStudents.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <StudentAvatar photoUrl={s.photo_url} fullName={s.full_name} className="w-10 h-10 rounded-xl" />
                        <div>
                          <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{s.full_name}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">ID: #{s.id.toString().padStart(4, '0')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 max-w-[220px]">
                        <div className="flex items-center gap-2 text-xs font-medium text-gray-500 truncate" title={s.email}>
                           <Mail size={12} className="text-gray-300 shrink-0" />
                           <span className="truncate">{s.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-medium text-gray-500 truncate" title={s.phone}>
                           <Phone size={12} className="text-gray-300 shrink-0" />
                           <span className="truncate">{s.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`status-badge ${s.visa_type === 'Student' ? 'status-active' : 'status-info'}`}>
                        {s.visa_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📍</span>
                        <span className="text-sm font-bold text-blue-900">{s.abroad_study_country || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs font-bold text-gray-700">{s.creator_name || 'System'}</p>
                      {s.modifier_name && s.modifier_name !== s.creator_name && (
                        <p className="text-[9px] text-indigo-500 font-bold uppercase tracking-tight">Modified by {s.modifier_name}</p>
                      )}
                      <p className="text-[10px] text-gray-400">{new Date(s.created_at).toLocaleDateString()}</p>
                    </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/students/${s.id}`)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            title="View Profile"
                          >
                            <FileText size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/students/${s.id}?edit=true`)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                            title="Edit Student"
                          >
                            <Edit3 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(s)}
                            className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                            title="Delete Student"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredStudents.length)} of {filteredStudents.length} entries
                  </p>
                  <div className="flex gap-2">
                    <button 
                      disabled={currentPage === 1} 
                      onClick={() => setCurrentPage(prev => prev - 1)}
                      className="p-2 bg-white border border-gray-100 rounded-xl disabled:opacity-30 hover:shadow-sm transition-all"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div className="flex items-center px-4 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-900">
                      {currentPage} / {totalPages}
                    </div>
                    <button 
                      disabled={currentPage === totalPages} 
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      className="p-2 bg-white border border-gray-100 rounded-xl disabled:opacity-30 hover:shadow-sm transition-all"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
              </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Students;
